import { useState, useEffect } from "react";
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { requestNotificationPermission, onForegroundMessage } from "../firebase/firebase";

export const useNotifications = () => {
  const [token, setToken] = useState(null);
  const [notification, setNotification] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  const saveTokenToServer = async (fcmToken) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');
    if (!user.id || !fcmToken || !token) return;
    const res = await fetch('https://elite-goats.onrender.com/api/auth/save-fcm-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ fcmToken, userId: user.id })
    });
    const data = await res.json();
    console.log('FCM save result:', data);
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // ── Android native push ──────────────────────────
      PushNotifications.requestPermissions().then(({ receive }) => {
        if (receive === 'granted') {
          PushNotifications.register();
        }
      });

      PushNotifications.addListener('registration', async (tok) => {
        console.log('📱 Native FCM token:', tok.value);
        setToken(tok.value);
        setPermissionStatus('granted');
        localStorage.setItem('fcm_token', tok.value);
        await saveTokenToServer(tok.value);
      });

      PushNotifications.addListener('registrationError', (err) => {
        console.error('Native push registration error:', err);
      });

      PushNotifications.addListener('pushNotificationReceived', (notif) => {
        setNotification({
          title: notif.title,
          body: notif.body,
          data: notif.data,
          timestamp: new Date()
        });
        setTimeout(() => setNotification(null), 5000);
      });

    } else {
      // ── Web browser FCM ──────────────────────────────
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        requestNotificationPermission().then((fcmToken) => {
          if (fcmToken) {
            setToken(fcmToken);
            setPermissionStatus('granted');
            localStorage.setItem('fcm_token', fcmToken);
            saveTokenToServer(fcmToken);
          }
        });
      }
    }
  }, []);

  const enableNotifications = async () => {
    if (Capacitor.isNativePlatform()) {
      const { receive } = await PushNotifications.requestPermissions();
      if (receive === 'granted') {
        await PushNotifications.register();
        setPermissionStatus('granted');
      }
      return null;
    }
    // Web
    const fcmToken = await requestNotificationPermission();
    if (fcmToken) {
      setToken(fcmToken);
      setPermissionStatus('granted');
      localStorage.setItem('fcm_token', fcmToken);
      await saveTokenToServer(fcmToken);
    } else {
      setPermissionStatus(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
    }
    return fcmToken;
  };

  // Foreground message listener (web only)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (permissionStatus !== 'granted') return;

    const unsubscribe = onForegroundMessage((payload) => {
      const notif = {
        id: Date.now(),
        title: payload.data?.title,
        body: payload.data?.body,
        animalName: payload.data?.animalName,
        time: new Date().toISOString(),
        read: false
      };
      const existing = JSON.parse(localStorage.getItem('admin_notifications') || '[]');
      localStorage.setItem('admin_notifications', JSON.stringify([notif, ...existing].slice(0, 20)));

      setNotification({
        title: payload.data?.title,
        body: payload.data?.body,
        data: payload.data,
        timestamp: new Date()
      });
      setTimeout(() => setNotification(null), 5000);
    });

    return () => unsubscribe();
  }, [permissionStatus]);

  return {
    token,
    notification,
    permissionStatus,
    enableNotifications,
    dismissNotification: () => setNotification(null)
  };
};